import { useTranslation } from 'react-i18next'
import { Drawer, IconButton, Button } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

interface FilterSheetProps {
  open: boolean
  onClose: () => void
  /** Model view and context selections that narrow the content. Level is excluded: it always has a value. */
  activeCount: number
  onClearAll: () => void
  children: React.ReactNode
}

/**
 * Bottom sheet host for the filter controls at `compact`.
 *
 * Presentational only: it receives the same `.filter-group` markup the wide bar
 * renders, so there is exactly one copy of the filter logic (in FilterBar).
 * The paper carries the filter bar's dark surface, which those groups' label and
 * select styling assumes.
 */
export function FilterSheet({ open, onClose, activeCount, onClearAll, children }: FilterSheetProps) {
  const { t } = useTranslation()

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: 'filter-sheet',
          // Same reason as MobileNavDrawer: MUI's own paper background is injected
          // after responsive.css, so the dark surface has to come from sx.
          sx: {
            backgroundColor: 'var(--color-brand-darker)',
            backgroundImage: 'none',
            color: 'var(--color-white)',
          },
        },
      }}
    >
      <div className="filter-sheet-header">
        <span className="filter-sheet-title">
          {activeCount > 0 ? t('filterBar.filtersApplied', { count: activeCount }) : t('filterBar.filters')}
        </span>
        <IconButton
          onClick={onClose}
          aria-label={t('filterBar.closeFilters')}
          sx={{ color: 'var(--color-on-dark-75)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-12)' } }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <div className="filter-sheet-body">{children}</div>

      <div className="filter-sheet-actions">
        <Button
          variant="text"
          onClick={onClearAll}
          disabled={activeCount === 0}
          sx={{ color: 'var(--color-on-dark-75)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' } }}
        >
          {t('filterBar.clearFilters')}
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: 'var(--color-white)',
            borderColor: 'var(--color-on-dark-60)',
            '&:hover': { borderColor: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' },
          }}
        >
          {t('filterBar.doneFilters')}
        </Button>
      </div>
    </Drawer>
  )
}
